import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { cn } from '../utils/classUtils';
import Card from './Card';
import Hover from './Hover';

export type CardValueVariant = 'bold' | 'regular';

export interface CardItem {
  name: React.ReactNode;
  key?: string;
  rightActionComponent?: React.ReactElement;
  subtitle?: string | number;
  url?: string;
  value?: string | number | React.ReactElement;
  ValueWrapper?: React.ComponentType;
  tooltipInfo?: string;
  tooltipIcon?: IconProp;
  expandedComponent?: React.ReactElement;
}
export interface CardWithItemsProps {
  items: CardItem[];
  title?: string;
  bottomAction?: string;
  onBottomActionClick?(): void;
  variant?: CardValueVariant;
  RightComponent?: React.ReactElement;
  BottomComponent?: React.ReactElement;
  children?: React.ReactNode;
  cardVariant?: 'border' | 'no-border';
  focusId?: string;
  titleIconPlacement?: 'left' | 'right';
  titleAdjacentComponent?: React.ReactElement;
  testId?: string;
}

const CardWithItems: React.FC<CardWithItemsProps> = ({
  items,
  bottomAction,
  onBottomActionClick,
  title,
  RightComponent,
  BottomComponent,
  focusId,
  cardVariant = 'border',
  children,
  titleIconPlacement,
  titleAdjacentComponent,
  testId,
}) => {
  return (
    <Card
      testId={testId}
      titleIconPlacement={titleIconPlacement}
      titleAdjacentComponent={titleAdjacentComponent}
      bottomAction={bottomAction}
      title={title}
      onBottomActionClick={onBottomActionClick}
      BottomComponent={BottomComponent}
      RightComponent={RightComponent}
      variant={cardVariant}
      focusId={focusId}
    >
      {items.map((item, index) => {
        return (
          <div
            key={item.key ?? item.name?.toString()}
            aria-label={item.key ?? item.name?.toString()}
          >
            {items.length + 1 !== index && index > 0 && (
              <div className='border-b' />
            )}

            <div className='px-4 py-2 justify-between flex-row flex items-center'>
              <div className='flex flex-col'>
                <div className='flex flex-row items-center space-x-1'>
                  <div>
                    {item.url ? (
                      <Link to={item.url}>
                        <span className='text-primary-blue font-inter-semibold text-base hover:underline'>
                          {item.name}
                        </span>
                      </Link>
                    ) : (
                      <span
                        className={cn(
                          'leading-5 font-inter-medium text-sm text-gray-600',
                        )}
                      >
                        {item.name}
                      </span>
                    )}
                  </div>
                  {item?.tooltipInfo && (
                    <Hover
                      hoverComponent={
                        <div className='font-inter-regular text-sm text-primary-dark px-2 max-w-[260px]'>
                          {item?.tooltipInfo}
                        </div>
                      }
                      config={{ placement: 'top' }}
                    >
                      <FontAwesomeIcon
                        icon={item?.tooltipIcon ?? faCircleExclamation}
                        size='lg'
                        className='text-dark'
                      />
                    </Hover>
                  )}
                </div>
                <span className='text-sm font-inter-regular text-gray-500'>
                  {item.subtitle}
                </span>
              </div>
              <div className='flex flex-row items-center space-x-2 justify-between'>
                <span
                  className={cn(
                    'leading-4 text-right text-sm font-inter-light text-primary-dar',
                  )}
                >
                  {item.value}
                </span>
                {!!item?.rightActionComponent && (
                  <div>{item?.rightActionComponent}</div>
                )}
              </div>
            </div>
            {!!item?.expandedComponent && (
              <div className='-mt-2 mb-2 px-4'>{item?.expandedComponent}</div>
            )}
          </div>
        );
      })}
      {children && <div>{children}</div>}
    </Card>
  );
};

export default CardWithItems;
